import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import { Link as LinkIcon, LinkOff as LinkOffIcon, Info as InfoIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../../../config/api';
import { isKnowledgeTool } from '../../../utils/toolVisibility';
import useIsMobile from '../useIsMobile';
import './AssistantSettingsTheme.css';
const KnowledgeSettings = ({ assistant, onUpdate }) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  // Tracks WHICH knowledge tool is in flight. A single boolean spun every
  // row's button at once, see the same fix in ToolsSettings.
  const [pendingToolId, setPendingToolId] = useState(null);
  const [allTools, setAllTools] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch all tools for the assistant
  useEffect(() => {
    const fetchTools = async () => {
      if (!assistant || !assistant.id) return;
      
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/${assistant.id}/tools`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch tools');
        }
        
        const data = await response.json();
        setAllTools(data);
      } catch (err) {
        setError('Failed to load tools. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, [assistant]);
  
  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleToggleToolLink = async (tool, shouldLink) => {
    try {
      setPendingToolId(tool.id);
      setError(null);
      const token = localStorage.getItem('access_token');
      const knowledgeId = tool?.configuration?.kb_id ?? tool?.configuration?.dataset_id;

      if (!knowledgeId) {
        throw new Error('Knowledge base id is missing for this tool');
      }

      const endpoint = `${API_BASE_URL}/api/knowledge/${knowledgeId}/agents/${assistant.id}`;
      const method = shouldLink ? 'POST' : 'DELETE';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update tool status');
      }

      // Re-fetch the updated tools list
      const refreshResponse = await fetch(`${API_BASE_URL}/api/${assistant.id}/tools`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      if (refreshResponse.ok) {
        const updatedTools = await refreshResponse.json();
        setAllTools(updatedTools);
      }

      showSuccessMessage(shouldLink ? 'Knowledge linked successfully' : 'Knowledge unlinked successfully');

      if (onUpdate) onUpdate();

    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error(err);
    } finally {
      setPendingToolId(null);
    }
  };

  const knowledgeTools = allTools.filter(isKnowledgeTool);
  const attachedTools = knowledgeTools.filter(tool => tool.is_linked === true);
  const unattachedTools = knowledgeTools.filter(tool => tool.is_linked === false || tool.is_linked === null);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Same row shape as ToolsSettings so linked knowledge and linked tools read
  // as one consistent pattern across the two tabs.
  const renderKnowledgeRow = (tool, isLinked) => {
    const isPending = pendingToolId === tool.id;
    return (
      <li className="assistantToolRow" key={tool.id}>
        <div className="assistantToolMain">
          <div className="assistantToolTitleRow">
            <span className="assistantToolName" title={tool.name}>{tool.name}</span>
            <span className="assistantToolBadge">Knowledge</span>
          </div>
          {!isMobile && (
            <p className="assistantToolDescription">
              {tool.description || 'Knowledge base search tool'}
            </p>
          )}
        </div>

        <Tooltip title={isLinked ? 'Unlink knowledge' : 'Link knowledge'}>
          <span className="assistantToolActionWrap">
            <IconButton
              aria-label={`${isLinked ? 'Unlink' : 'Link'} ${tool.name}`}
              onClick={() => handleToggleToolLink(tool, !isLinked)}
              disabled={isPending}
              className="assistantIconButtonNeutral"
            >
              {isPending
                ? <CircularProgress size={18} />
                : isLinked ? <LinkOffIcon /> : <LinkIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </li>
    );
  };

  return (
    <Box className="assistantSettingsRoot">

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
      
      {/* Currently attached knowledge */}
      <Paper elevation={0} className="assistantSettingsPanel">
        <div className="assistantPanelHead">
          <Typography variant="h6" className="assistantPanelTitle">Linked Knowledge</Typography>
          {attachedTools.length > 0 && (
            <span className="assistantPanelCount">{attachedTools.length}</span>
          )}
        </div>
        {attachedTools.length === 0 ? (
          <p className="assistantPanelEmpty">
            No knowledge base is linked yet. Link one below so this agent can
            answer from your own content.
          </p>
        ) : (
          <ul className="assistantToolList">
            {attachedTools.map((tool) => renderKnowledgeRow(tool, true))}
          </ul>
        )}
      </Paper>

      {/* Available knowledge to attach */}
      <Paper elevation={0} className="assistantSettingsPanel">
        <div className="assistantPanelHead">
          <Typography variant="h6" className="assistantPanelTitle">Available Knowledge</Typography>
          {unattachedTools.length > 0 && (
            <span className="assistantPanelCount">{unattachedTools.length}</span>
          )}
        </div>
        {unattachedTools.length === 0 ? (
          <p className="assistantPanelEmpty">All available knowledge bases are linked.</p>
        ) : (
          <ul className="assistantToolList">
            {unattachedTools.map((tool) => renderKnowledgeRow(tool, false))}
          </ul>
        )}
      </Paper>

      <Box className="assistantHintRow">
        <InfoIcon fontSize="small" />
        <Typography variant="body2">
          Linked knowledge lets the agent search those datasets when answering.
        </Typography>
      </Box>
    </Box>
  );
};

export default KnowledgeSettings;
